/**
 * Operator authentication (T-029).
 *
 * A real login against `POST /operator/login`: argon2id on the server, a
 * short-lived access token presented as `Authorization: Bearer`, and a rotating
 * refresh token to keep the shift alive. Identity comes from the verified
 * token on every request — the server takes `reviewedBy` from it and rejects
 * any body that still carries an `operatorId`.
 *
 * The session is held in memory only, never in localStorage: the access token
 * releases real money, and a bearer credential that survives the tab is a
 * credential someone else's tab can replay. Closing the panel is signing out.
 *
 * Everything about operator identity lives in this one module, so the rest of
 * the app only ever sees `authHeaders()` and `currentOperator()`.
 */

const BASE = ((import.meta.env['VITE_API_BASE'] as string | undefined) ?? '').replace(/\/$/, '');

export type OperatorRole = 'viewer' | 'approver' | 'admin';

export interface OperatorInfo {
  id: number;
  username: string;
  email: string;
  role: OperatorRole;
}

/** Second-factor state for the signed-in operator (ADR-046). */
export interface MfaState {
  enrolled: boolean;
  enrollmentRequired: boolean;
}

/** Session state. Deliberately module-local and in memory only. */
let accessToken = '';
let refreshToken = '';
let operator: OperatorInfo | null = null;
let mfa: MfaState = { enrolled: false, enrollmentRequired: false };

/** Called when the session ends for any reason, so the shell can show the login. */
let onSessionEnded: (() => void) | null = null;

export function setSessionEndedListener(cb: (() => void) | null): void {
  onSessionEnded = cb;
}

export function hasCredential(): boolean {
  return accessToken.length > 0;
}

export function currentOperator(): OperatorInfo | null {
  return operator;
}

/** The signed-in operator's second-factor state (ADR-046). */
export function currentMfa(): MfaState {
  return mfa;
}

/** After enrolling+confirming, reflect it without a full re-login. */
export function markMfaEnrolled(): void {
  mfa = { enrolled: true, enrollmentRequired: false };
}

/**
 * The only place a credential becomes a header. Everything the panel sends
 * goes through this.
 */
export function authHeaders(): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

interface SessionResponse {
  accessToken: string;
  refreshToken: string;
  operator: OperatorInfo;
  mfa?: MfaState;
}

/** 401 with this code means the account is enrolled and needs a TOTP code. */
export class TotpNeeded extends Error {
  constructor(readonly invalid: boolean) {
    super(invalid ? 'That code is not valid.' : 'Enter your authenticator code.');
    this.name = 'TotpNeeded';
  }
}

export async function login(
  username: string,
  password: string,
  totpCode?: string,
): Promise<OperatorInfo> {
  const response = await fetch(`${BASE}/operator/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      username,
      password,
      ...(totpCode ? { totpCode } : {}),
    }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string; message?: string } | null;
    // A second factor is enrolled: surface a distinct error so the form can
    // prompt for the code rather than re-collecting the password (ADR-046).
    if (body?.error === 'totp_required') throw new TotpNeeded(false);
    if (body?.error === 'totp_invalid') throw new TotpNeeded(true);
    throw new Error(body?.message ?? `Sign in failed (${response.status}).`);
  }

  const session = (await response.json()) as SessionResponse;
  accessToken = session.accessToken;
  refreshToken = session.refreshToken;
  operator = session.operator;
  mfa = session.mfa ?? { enrolled: false, enrollmentRequired: false };
  return session.operator;
}

/**
 * Rotate the session once. Used by the HTTP client when an access token
 * expires mid-shift; a refusal ends the session rather than retrying forever.
 */
export async function tryRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  try {
    const response = await fetch(`${BASE}/operator/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      endSession();
      return false;
    }
    const session = (await response.json()) as SessionResponse;
    accessToken = session.accessToken;
    refreshToken = session.refreshToken;
    operator = session.operator;
    if (session.mfa) mfa = session.mfa;
    return true;
  } catch {
    // Network trouble is not a revoked session; keep the tokens and let the
    // caller surface the error.
    return false;
  }
}

/** Revoke the refresh token server-side (best effort) and forget everything. */
export function signOut(): void {
  const token = refreshToken;
  if (token) {
    void fetch(`${BASE}/operator/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token }),
    }).catch(() => {});
  }
  endSession();
}

function endSession(): void {
  accessToken = '';
  refreshToken = '';
  operator = null;
  mfa = { enrolled: false, enrollmentRequired: false };
  onSessionEnded?.();
}
